import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { formatOrderStatus } from '../../utils/orderStatus';
import { useAuth } from '../../context/AuthContext';
import { useSSE } from '../../hooks/useSSE';
import { Navbar } from '../../components/Navbar';
import { LiveDriverMap } from '../../components/LiveDriverMap';
import { RecentlyViewedCustomersStrip } from '../../components/RecentlyViewedCustomersStrip';
import { formatOrderTimestamp } from '../../utils/orderTimestamp';
import { usePendingOrderAudio } from '../../hooks/usePendingOrderAudio';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { 
  Package, 
  Users, 
  Box,
  DollarSign,
  Clock,
  Truck,
  CheckCircle,
  XCircle,
  AlertTriangle,
  MapPin,
  Map,
  Zap,
  Volume2,
  VolumeX,
  Radio,
  Pencil,
  Trash2,
  Inbox,
  MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { geocodeAddress, haversineMiles } from '../../utils/mapbox';
import { playNewOrderBeep, isAlertsMuted, setAlertsMuted } from '../../utils/alerts';
import { EditOrderItemsDialog } from '../../components/EditOrderItemsDialog';
import { DeliverySlotBadge } from '../../components/DeliverySlotBadge';
import { OrderSortToggle } from '../../components/OrderSortToggle';
import { sortOrders, isDueSoon } from '../../utils/orderSort';
import { OpenKitDialog } from '../../components/OpenKitDialog';
import { KitItemBar, kitTotalValue } from '../../components/KitItemBar';
import { KitReconcileDialog } from '../../components/KitReconcileDialog';
import { RegionBadge } from '../../components/RegionBadge';
import { ActivityFeedPanel } from '../../components/ActivityFeedPanel';
import { ActiveCartsPanel } from '../../components/ActiveCartsPanel';
import { InboxPanel } from '../../components/InboxPanel';
import DispatcherChatPanel from '../../components/DispatcherChatPanel';
import { DispatcherHeaderMenu } from '../../components/DispatcherHeaderMenu';
import { HealthBadge } from '../../components/HealthBadge';
import {
  OrderFiltersBar,
  applyOrderFilters,
  countActiveFilters,
  EMPTY_FILTERS,
} from '../../components/OrderFiltersBar';
import { BulkActionsMenu } from '../../components/BulkActionsMenu';
import { useFlag } from '../../context/FeatureFlagsContext';
import { useRegionScope, filterByRegionScope } from '../../context/RegionContext';
// Feb 2026 — Client-side driver-to-customer distance/ETA computation.
// Refreshes instantly on every SSE `order.location_updated` event; no
// extra API round-trip per order-card render.
import { driverToCustomerEta } from '../../utils/geo';
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export function DispatchDashboard() {
  const { user } = useAuth();
  const { selectedRegion, setSelectedRegion, isSuperAdmin } = useRegionScope();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [orderFilters, setOrderFilters] = useState(EMPTY_FILTERS);
  const [orderSort, setOrderSort] = useState(() => localStorage.getItem('dispatch_order_sort') === 'delivery' ? 'delivery' : 'newest');
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNowTick(Date.now()), 60000); return () => clearInterval(t); }, []);
  const [drivers, setDrivers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('orders');
  // Phase 5.9.15 — deep-link to a tab via ?tab=... (used by ticket push
  // notifications that should land you straight in the Inbox).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && ['orders', 'map', 'staff', 'kits', 'inventory', 'activity', 'inbox', 'chat'].includes(t)) {
      setSelectedTab(t);
      // Clean the URL after deep-linking so further tab clicks aren't sticky.
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    }
    // Phase 5.9.22 — capture `?orderId=...` once, on first mount. The real
    // open happens in a separate effect that waits for orders to load.
    const oid = searchParams.get('orderId');
    if (oid && orderDeepLinkRef.current === null) {
      orderDeepLinkRef.current = oid;
      setSelectedTab('orders');
      const next = new URLSearchParams(searchParams);
      next.delete('orderId');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [orderCoords, setOrderCoords] = useState({}); // { [orderId]: {lat,lng} }
  const [reassigning, setReassigning] = useState({}); // { [orderId]: true } — dispatcher clicked CHANGE
  const [flashingIds, setFlashingIds] = useState({}); // { [orderId]: 'new' | 'updated' }
  const [flashTicks, setFlashTicks] = useState({});   // { [orderId]: N } — bumped each SSE event to restart the LIVE ring animation
  const [muted, setMuted] = useState(isAlertsMuted());
  const [sseConnected, setSseConnected] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null); // order being edited
  // Phase 5.9.22 — `?orderId=` deep-link support. Powered by the "RECENT ORDERS"
  // strip on the User Management drawer (UsersAdminPage). Lands the operator on
  // the dispatch dashboard and auto-opens the order detail dialog once orders
  // are loaded. Clears the param afterwards so refresh/back doesn't re-open.
  // Also forces the "orders" tab to be active.
  const orderDeepLinkRef = useRef(null);
  // Phase 5.9.22 — once orders arrive after a deep link, locate and open the
  // requested one. Falls back to a fetch-by-id if it's not in the in-memory
  // list (e.g. cancelled/old orders that the dashboard filter excludes).
  useEffect(() => {
    const target = orderDeepLinkRef.current;
    if (!target || !orders.length) return;
    const found = orders.find((o) => o.id === target);
    if (found) {
      setEditingOrder(found);
      orderDeepLinkRef.current = null;
      return;
    }
    // Not in the active list — fetch directly so the operator can still see
    // cancelled / completed orders linked from User Management.
    orderDeepLinkRef.current = null;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/orders/${target}`, { withCredentials: true });
        if (data) setEditingOrder(data);
      } catch (e) {
        toast.error(`Could not open order #${target.slice(0, 8).toUpperCase()}`);
      }
    })();
  }, [orders]);
  // Phase 6 Tier 1 (Feb 2026) — Audio alerts for pending orders.
  // Loud-by-default. Toggleable via dispatch header button.
  const [audioEnabled, setAudioEnabled] = useState(() => {
    try { return localStorage.getItem('dispatch_audio_alerts') !== 'off'; }
    catch (_e) { return true; }
  });
  const toggleAudio = () => {
    setAudioEnabled((prev) => {
      const next = !prev;
      try { localStorage.setItem('dispatch_audio_alerts', next ? 'on' : 'off'); } catch (_e) {}
      return next;
    });
  };
  // Tick state to force re-render every 60s so the "oldest pending age" badge
  // and urgency color update live without waiting for the next SSE/poll cycle.
  const [, setAgeTick] = useState(0);
  // Pending count is needed for the audio hook (called at top level). We
  // recompute it via the same .filter() below — cheap, and avoids reordering
  // hook calls relative to the early-return loading guard.
  const pendingCountForAudio = orders.filter(o => o.status === 'pending').length;
  const audio = usePendingOrderAudio(pendingCountForAudio, { enabled: audioEnabled });
  useEffect(() => {
    if (pendingCountForAudio === 0) return undefined;
    const id = setInterval(() => setAgeTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, [pendingCountForAudio]);
  const audioAlertsFlag = useFlag('dispatcher.audio_alerts', true);
  const autoAssignEnabled = useFlag('dispatcher.auto_assign', true);
  const editOrderEnabled = useFlag('dispatcher.edit_order', true);
  const deleteOrderEnabled = useFlag('dispatcher.delete_order', true);
  const broadcastEnabled = useFlag('dispatcher.broadcast', true);
  const kitsEnabled = useFlag('kits.enabled', true);
  const [kits, setKits] = useState([]);
  const [openKitOpen, setOpenKitOpen] = useState(false);
  const [reconcileKit, setReconcileKit] = useState(null);
  const geocodingRef = useRef({}); // track in-flight geocodes
  const flashTimersRef = useRef({});

  // Feb 2026 (Phase 3) — bind the dispatch orders list to the shared
  // React Query cache so the bottom-tab prefetch primes THIS page. The
  // existing `orders` useState still drives all downstream UI (search,
  // filters, optimistic edits) but is now hydrated from the query on
  // arrival. Removed the 30s polling interval — SSE covers live updates
  // and refetchOnWindowFocus (queryClient default) covers stale returns.
  const queryClient = useQueryClient();
  const ordersQuery = useQuery({
    queryKey: ['dispatch', 'orders'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/orders`, { withCredentials: true });
      return data || [];
    },
  });
  useEffect(() => {
    // Hydrate local state from the query cache — either from a prefetch
    // (instant paint) or from the initial network fetch.
    if (ordersQuery.data) setOrders(ordersQuery.data);
  }, [ordersQuery.data]);

  const fetchData = useCallback(async () => {
    try {
      // Phase 5.9.11 — SA can scope stats to a region via the header switcher.
      // Regional dispatchers ignore this param server-side anyway.
      const statsUrl = isSuperAdmin && selectedRegion && selectedRegion !== 'all'
        ? `${API_URL}/api/stats/dashboard?region=${encodeURIComponent(selectedRegion)}`
        : `${API_URL}/api/stats/dashboard`;
      // Feb 2026 — Orders are managed by the ['dispatch','orders'] query
      // above. fetchData no longer refetches orders (that used to cause a
      // request storm when combined with useEffect-driven fetchData
      // re-runs). React Query handles refresh via refetchOnWindowFocus +
      // the SSE cache bridge; a manual bust is queryClient.invalidateQueries.
      const [statsRes, driversRes, productsRes] = await Promise.all([
        axios.get(statsUrl, { withCredentials: true }),
        axios.get(`${API_URL}/api/drivers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/products`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setDrivers(driversRes.data);
      setProducts(productsRes.data);
      // Kits — best-effort, only if feature enabled (graceful 401 handling)
      try {
        const kitsRes = await axios.get(`${API_URL}/api/kits`, { withCredentials: true });
        setKits(kitsRes.data?.kits || []);
      } catch (_) {
        setKits([]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, selectedRegion]);

  useEffect(() => {
    fetchData();
    // Feb 2026 — dropped the 30s setInterval polling. React Query's
    // refetchOnWindowFocus + SSE-driven setQueryData patches cover live
    // updates; the polling was a duplicate fetch after Phase 4 landed.
  }, [fetchData]);

  // Feb 2026 (Latency Fix #4) — SSE-storm debouncing.
  // Rapid bursts of `order.created`, `driver.location`, or `kit.*` events used
  // to trigger a full refetch PER EVENT, hammering the network on busy days.
  // These wrappers coalesce trailing calls so multiple events within the same
  // window result in exactly one HTTP round-trip.
  //
  //   • fetchData          debounced 600ms  (full dashboard refresh)
  //   • refetchDrivers     debounced 500ms  (driver list only)
  //   • refetchKits        debounced 750ms  (kit list only)
  const debouncedFetchData = useDebouncedCallback(fetchData, 600);
  const debouncedRefetchDrivers = useDebouncedCallback(() => {
    axios.get(`${API_URL}/api/drivers`, { withCredentials: true })
      .then(r => setDrivers(r.data))
      .catch(() => {});
  }, 500);
  const debouncedRefetchKits = useDebouncedCallback(() => {
    axios.get(`${API_URL}/api/kits`, { withCredentials: true })
      .then(r => setKits(r.data?.kits || []))
      .catch(() => {});
  }, 750);

  // Flash a row briefly (new/updated) then clear it.
  // Feb 2026 — Fluidity LIVE ring: also bump `flashTicks[orderId]` so the
  // overlay's React `key` changes on every SSE event. That forces an
  // unmount + remount of the small overlay div, which restarts the CSS
  // `flash-live` animation from t=0 even when the row was already in the
  // flashing state (e.g. rapid back-to-back status toggles). Without this
  // bump, only the FIRST SSE event in a burst would visibly pulse.
  const flashRow = useCallback((orderId, kind = 'updated') => {
    setFlashingIds(prev => ({ ...prev, [orderId]: kind }));
    setFlashTicks(prev => ({ ...prev, [orderId]: (prev[orderId] || 0) + 1 }));
    if (flashTimersRef.current[orderId]) clearTimeout(flashTimersRef.current[orderId]);
    flashTimersRef.current[orderId] = setTimeout(() => {
      setFlashingIds(prev => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
    }, kind === 'new' ? 6000 : 2500);
  }, []);

  // Real-time stream — dispatcher-only SSE
  useSSE(useCallback((evt) => {
    if (!evt || !evt.type) return;
    setSseConnected(true);

    switch (evt.type) {
      case 'stream.ready': {
        // Connection established, badge flips to LIVE
        break;
      }
      case 'order.created': {
        const order = evt.order;
        if (!order) return;
        setOrders(prev => {
          if (prev.some(o => o.id === order.id)) return prev;
          return [order, ...prev];
        });
        flashRow(order.id, 'new');
        if (audioAlertsFlag) playNewOrderBeep();
        toast.success(`New order: ${order.customer_name} — $${Number(order.total || 0).toFixed(2)}`, {
          description: order.delivery_address,
          duration: 6000,
        });
        // Feb 2026 — removed the debouncedFetchData() refetch for order.created.
        // The SSE cache bridge (useSSECacheBridge) has already prepended the
        // new order into ['dispatch','orders'] via setQueryData; refreshing
        // via fetchData would cause the exact request storm reported in
        // bug_verification_62.json. Stats/drivers/products are unaffected
        // by a single order.created, so no refresh is needed here.
        break;
      }
      case 'order.status_updated': {
        setOrders(prev => prev.map(o =>
          o.id === evt.order_id ? { ...o, status: evt.status, updated_at: new Date().toISOString() } : o
        ));
        flashRow(evt.order_id, 'updated');
        break;
      }
      case 'order.assigned': {
        setOrders(prev => prev.map(o =>
          o.id === evt.order_id
            ? { ...o, driver_id: evt.driver_id, driver_name: evt.driver_name }
            : o
        ));
        flashRow(evt.order_id, 'updated');
        break;
      }
      case 'order.unassigned': {
        setOrders(prev => prev.map(o =>
          o.id === evt.order_id
            ? { ...o, driver_id: null, driver_name: null, driver_location: null, status: evt.status || o.status }
            : o
        ));
        flashRow(evt.order_id, 'updated');
        break;
      }
      case 'order.payment_updated': {
        setOrders(prev => prev.map(o =>
          o.id === evt.order_id
            ? { ...o, payment_status: evt.payment_status, paid_by: evt.paid_by || null, updated_at: new Date().toISOString() }
            : o
        ));
        flashRow(evt.order_id, 'updated');
        break;
      }
      case 'order.items_updated': {
        setOrders(prev => prev.map(o =>
          o.id === evt.order_id
            ? { ...o, items: evt.items, total: evt.total, updated_at: new Date().toISOString() }
            : o
        ));
        flashRow(evt.order_id, 'updated');
        break;
      }
      case 'order.details_updated': {
        setOrders(prev => prev.map(o =>
          o.id === evt.order_id
            ? { ...o, ...(evt.delivery_address ? { delivery_address: evt.delivery_address } : {}),
                       ...(evt.payment_method ? { payment_method: evt.payment_method } : {}),
                       ...(evt.notes !== undefined ? { notes: evt.notes } : {}),
                       updated_at: new Date().toISOString() }
            : o
        ));
        flashRow(evt.order_id, 'updated');
        break;
      }
      case 'order.deleted': {
        setOrders(prev => prev.filter(o => o.id !== evt.order_id));
        break;
      }
      case 'order.bulk_deleted': {
        const removed = new Set(evt.order_ids || []);
        setOrders(prev => prev.filter(o => !removed.has(o.id)));
        break;
      }
      case 'order.location_updated': {
        setOrders(prev => prev.map(o =>
          o.id === evt.order_id ? { ...o, driver_location: evt.location } : o
        ));
        break;
      }
      case 'driver.online':
      case 'driver.offline':
      case 'driver.location':
      case 'driver.mode': {
        // Debounced lightweight refresh of just drivers (not the whole
        // dashboard). Coalesces frequent live-location pings.
        debouncedRefetchDrivers();
        break;
      }
      case 'kit.opened':
      case 'kit.transferred':
      case 'kit.updated': {
        debouncedRefetchKits();
        break;
      }
      default:
        break;
    }
  }, [flashRow, debouncedFetchData, debouncedRefetchDrivers, debouncedRefetchKits]));

  // Geocode delivery addresses for any unassigned OR editable orders
  // so we can show distance to each driver & auto-assign closest.
  useEffect(() => {
    const editable = orders.filter(
      o => ['pending', 'confirmed', 'preparing'].includes(o.status) && o.delivery_address
    );
    editable.forEach(async (o) => {
      if (orderCoords[o.id] || geocodingRef.current[o.id]) return;
      geocodingRef.current[o.id] = true;
      const coords = await geocodeAddress(o.delivery_address);
      if (coords) {
        setOrderCoords(prev => ({ ...prev, [o.id]: coords }));
      }
    });
  }, [orders, orderCoords]);

  // Feb 2026 — a driver with an active out_for_delivery order is counted as
  // online for ALL dispatcher UI (map, counters, idle calc) even if their
  // heartbeat just briefly stalled. Backend stamps `has_active_route:true`
  // on the same condition, so we trust that flag too. Fixes the "1 On
  // Route / 0 Idle / NO DRIVERS ONLINE" contradiction.
  const onlineDrivers = drivers.filter(d =>
    (d.online || d.has_active_route) && d.current_location
  );

  // Apply super-admin region scope filter (regional dispatchers already get
  // backend-filtered data; this is purely UX for super-admin's "switch view").
  const visibleOrders = filterByRegionScope(orders, selectedRegion);
  const filteredOrders = applyOrderFilters(visibleOrders, orderFilters);
  const sortedOrders = sortOrders(filteredOrders, orderSort, nowTick);
  const visibleDrivers = filterByRegionScope(drivers, selectedRegion);
  const visibleOnlineDrivers = filterByRegionScope(onlineDrivers, selectedRegion);
  const visibleKits = filterByRegionScope(kits, selectedRegion);

  // For region-aware stats strip in super-admin ALL mode
  const ordersByRegion = orders.reduce((acc, o) => {
    const r = o.region || 'unknown';
    acc[r] = (acc[r] || 0) + 1;
    return acc;
  }, {});

  const rankedDriversForOrder = (orderId) => {
    const dest = orderCoords[orderId];
    const order = orders.find(o => o.id === orderId);
    const computeCoverage = (driver) => {
      // Look up the driver's active kit in our local state
      const kit = kits.find(k => k.driver_id === driver.id);
      if (!kit || !order) return null;
      const have = Object.fromEntries(kit.items.map(i => [i.product_id, i.qty_current]));
      const missing = order.items.filter(i => (have[i.product_id] || 0) < i.quantity);
      return { covers: missing.length === 0, missing };
    };
    // In delivery mode only — restock-mode drivers should not be assigned customer orders
    // Also: only consider drivers whose region matches the order's region (super-admin sees all,
    // backend will reject cross-region assignments anyway, but UX is cleaner this way)
    const eligible = onlineDrivers.filter(
      d => d.mode !== 'restock' && (!order?.region || !d.region || d.region === order.region)
    );
    if (!dest) return eligible.map(d => ({ driver: d, miles: null, coverage: computeCoverage(d) }));
    return eligible
      .map(d => ({
        driver: d,
        miles: haversineMiles(d.current_location, dest),
        coverage: computeCoverage(d),
      }))
      .sort((a, b) => {
        // Prefer drivers with full coverage; among same-coverage, sort by distance
        if ((a.coverage?.covers ? 1 : 0) !== (b.coverage?.covers ? 1 : 0)) {
          return (b.coverage?.covers ? 1 : 0) - (a.coverage?.covers ? 1 : 0);
        }
        return (a.miles ?? 1e9) - (b.miles ?? 1e9);
      });
  };

  const handleAutoAssignClosest = async (orderId) => {
    const ranked = rankedDriversForOrder(orderId);
    if (ranked.length === 0 || !ranked[0].driver) {
      toast.error('No online drivers available');
      return;
    }
    const best = ranked[0];
    const covers = best.coverage?.covers !== false; // null treated as ok
    if (best.miles == null) {
      toast.info(`Assigning ${best.driver.name} (location unknown)${covers ? '' : ' — needs restock'}`);
    } else {
      toast.success(`Assigning ${best.driver.name} — ${Number(best.miles ?? 0).toFixed(1)} mi${covers ? '' : ' · needs restock'}`);
    }
    await handleAssignDriver(orderId, best.driver.id);
  };

  const handleAssignDriver = async (orderId, driverId) => {
    try {
      await axios.patch(`${API_URL}/api/orders/${orderId}/assign`, { driver_id: driverId }, { withCredentials: true });
      toast.success('Driver assigned successfully');
      setReassigning(prev => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign driver');
    }
  };

  const handleUnassignDriver = async (orderId, currentDriverName) => {
    const short = orderId.slice(0, 8).toUpperCase();
    const who = currentDriverName ? ` from ${currentDriverName}` : '';
    if (!window.confirm(`Unassign driver${who} on order #${short}?\n\nThe order will return to "pending" and reappear in the assignable queue. The driver will be notified.`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/orders/${orderId}/assign`, { withCredentials: true });
      toast.success(`Driver unassigned from #${short}`);
      setReassigning(prev => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unassign driver');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    const short = orderId.slice(0, 8).toUpperCase();
    if (!window.confirm(`Permanently delete order #${short}? This cannot be undone. (Stock will be restored if pre-delivery.)`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/orders/${orderId}`, { withCredentials: true });
      toast.success(`Order #${short} deleted`);
      // Optimistic local removal — SSE order.deleted will keep other dispatchers in sync
      setOrders(prev => prev.filter(o => o.id !== orderId));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete order');
    }
  };

  const handleBulkDelete = async (status, olderThanDays) => {
    const matching = orders.filter(o => {
      if (o.status !== status) return false;
      if (olderThanDays != null) {
        const created = new Date(o.created_at);
        if (Date.now() - created.getTime() < olderThanDays * 86400000) return false;
      }
      return true;
    });
    if (matching.length === 0) {
      toast.info(`No ${status} orders${olderThanDays ? ` older than ${olderThanDays} days` : ''} to delete`);
      return;
    }
    const label = olderThanDays
      ? `${matching.length} ${status} orders older than ${olderThanDays} days`
      : `${matching.length} ${status} orders`;
    if (!window.confirm(`Permanently delete ${label}? This cannot be undone.`)) return;
    try {
      const payload = { status };
      if (olderThanDays != null) payload.older_than_days = olderThanDays;
      const { data } = await axios.post(
        `${API_URL}/api/orders/bulk-delete`,
        payload,
        { withCredentials: true }
      );
      toast.success(`Deleted ${data.deleted_count} ${status} order(s)`);
      const removed = new Set(data.deleted_ids || []);
      setOrders(prev => prev.filter(o => !removed.has(o.id)));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    }
  };

  const handleMarkPayment = async (orderId, newStatus) => {
    const verb = newStatus === 'paid' ? 'PAID' : newStatus === 'refunded' ? 'REFUNDED' : 'PENDING';
    const note = window.prompt(`Optional note for ${verb} (Cash App reference, etc):`, '') || '';
    try {
      await axios.patch(
        `${API_URL}/api/orders/${orderId}/payment`,
        { status: newStatus, note },
        { withCredentials: true },
      );
      toast.success(`Marked ${verb}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update payment');
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/orders/${orderId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Order status updated to ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-[rgba(255,159,10,0.1)] text-[#FF9F0A] border-[#FF9F0A]',
      confirmed: 'bg-[rgba(0,240,255,0.1)] text-[#00F0FF] border-[#00F0FF]',
      preparing: 'bg-[rgba(0,240,255,0.1)] text-[#00F0FF] border-[#00F0FF]',
      out_for_delivery: 'bg-[rgba(57,255,20,0.1)] text-[#39FF14] border-[#39FF14]',
      delivered: 'bg-[rgba(57,255,20,0.1)] text-[#39FF14] border-[#39FF14]',
      cancelled: 'bg-[rgba(255,59,48,0.1)] text-[#FF3B30] border-[#FF3B30]'
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050003]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-[#39FF14] font-mono">
            <span className="cursor-blink">█</span> LOADING_DISPATCH_TERMINAL...
          </div>
        </div>
      </div>
    );
  }

  const pendingOrders = visibleOrders.filter(o => o.status === 'pending');
  const activeOrders = visibleOrders.filter(o => ['confirmed', 'preparing', 'out_for_delivery'].includes(o.status));
  const lowStockProducts = products.filter(p => p.stock < 10);

  // Compute age (minutes) of the oldest pending order for the urgency badge.
  const oldestPendingAgeMin = pendingOrders.length === 0 ? 0 : Math.floor(
    Math.max(
      ...pendingOrders.map(o => {
        const t = new Date(o.created_at).getTime();
        return Number.isFinite(t) ? (Date.now() - t) / 60000 : 0;
      })
    )
  );
  const urgencyColor =
    oldestPendingAgeMin >= 7 ? '#FF3B30' :      // red — escalation territory
    oldestPendingAgeMin >= 3 ? '#FF9F0A' :      // amber — getting late
    '#39FF14';                                  // green — fresh

  return (
    <div className="min-h-screen bg-[#050003]" data-testid="dispatch-dashboard">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 gap-3 flex-wrap">
          <div>
            <h1 className="text-[#39FF14] font-mono text-2xl font-bold neon-text">
              {'>'} DISPATCH_CONTROL_CENTER
            </h1>
            <p className="text-[#4B6B52] font-mono text-sm mt-1">// ADMIN: {user?.email}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="dispatch-header-actions">
            {/* 1) Tiny LIVE dot — passive status, no text */}
            <span
              className="flex items-center justify-center w-7 h-7 border rounded-none"
              data-testid="sse-status"
              title={sseConnected ? 'Real-time stream connected' : 'Reconnecting to real-time stream…'}
              style={{
                borderColor: sseConnected ? '#39FF14' : '#FF9F0A',
                background: sseConnected ? 'rgba(57,255,20,0.08)' : 'rgba(255,159,10,0.08)',
              }}
            >
              <span
                className={sseConnected ? 'animate-pulse' : ''}
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: sseConnected ? '#39FF14' : '#FF9F0A',
                  boxShadow: sseConnected ? '0 0 6px rgba(57,255,20,0.8)' : 'none',
                }}
              />
            </span>

            {/* 2) ALERTS — icon-only on mobile, full button on desktop */}
            <Button
              variant="outline"
              onClick={() => {
                const next = !muted;
                setMuted(next);
                setAlertsMuted(next);
                toast.info(next ? 'Alerts muted' : 'Alerts unmuted');
                if (!next) playNewOrderBeep();
              }}
              className={`rounded-none font-mono h-9 px-2 sm:px-3 ${
                muted
                  ? 'border-[#4B6B52] text-[#4B6B52] hover:border-[#39FF14] hover:text-[#39FF14]'
                  : 'border-[#39FF14] text-[#39FF14] hover:bg-[rgba(57,255,20,0.08)]'
              }`}
              data-testid="toggle-alerts-btn"
              title={muted ? 'Unmute new-order alerts' : 'Mute new-order alerts'}
              aria-label={muted ? 'Unmute alerts' : 'Mute alerts'}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              <span className="hidden sm:inline ml-2">{muted ? 'MUTED' : 'ALERTS'}</span>
            </Button>

            {/* 3) REGION CONTEXT — super-admin gets a region dropdown, regional dispatcher gets a read-only chip. Both render in the same slot so visual rhythm stays consistent. */}
            <RegionBadge />

            {/* 4) ACTIVE CARTS pill (Phase 5.9 Stage 2) */}
            <ActiveCartsPanel isSuperAdmin={isSuperAdmin} selectedRegion={selectedRegion} />

            {/* 5) MANAGE INVENTORY — green hero CTA */}
            <Link to="/dispatch/inventory" data-testid="manage-inventory-link">
              <Button className="bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] rounded-none h-9 px-2 sm:px-3">
                <Box className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">MANAGE INVENTORY</span>
                <span className="sm:hidden">STOCK</span>
              </Button>
            </Link>

            {/* Hamburger menu — everything else */}
            <DispatcherHeaderMenu broadcastEnabled={broadcastEnabled} />
            {/* Sentry health pulse — SA only, polls every 60s */}
            <HealthBadge />
          </div>
        </div>

        {/* Cross-region overview (super-admin in ALL mode) */}
        {isSuperAdmin && selectedRegion === 'all' && Object.keys(ordersByRegion).length > 0 && (
          <div className="bg-[#0C0612] border border-[#00F0FF] p-4 mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="all-regions-strip">
            <p className="text-[#00F0FF] font-mono text-xs uppercase">// CROSS-REGION VIEW</p>
            <div className="flex gap-4">
              {Object.entries(ordersByRegion).map(([code, count]) => (
                <span key={code} className="text-[#39FF14] font-mono text-sm" data-testid={`region-stat-${code}`}>
                  {code.toUpperCase()}: {count} order{count === 1 ? '' : 's'}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Stats Grid */}
        {/* Phase 6 Tier 1 (Feb 2026) — surface the scope so operators can
            tell at a glance whether they're seeing single-region or cross-region
            totals. Fixed bug: DMV dispatchers were seeing NJ totals mixed in. */}
        {stats?.region_scope && (
          <div className="mb-2 font-mono text-[10px] text-[#6B8E73] uppercase" data-testid="stats-region-scope">
            // scope: <span className="text-[#39FF14]">{stats.region_scope.toUpperCase()} only</span>
          </div>
        )}
        {stats && stats.region_scope === null && (
          <div className="mb-2 font-mono text-[10px] text-[#6B8E73] uppercase" data-testid="stats-region-scope">
            // scope: <span className="text-[#00F0FF]">ALL REGIONS (cross-region)</span>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-px bg-[#2A0F3D] mb-8">
          <StatCard icon={Package} label="Total Orders" value={stats?.total_orders || 0} />
          <StatCard icon={Clock} label="Pending" value={stats?.pending_orders || 0} alert={stats?.pending_orders > 0} />
          <StatCard icon={Truck} label="Active" value={stats?.active_orders || 0} />
          <StatCard icon={CheckCircle} label="Delivered" value={stats?.delivered_orders || 0} />
          <StatCard icon={Users} label="Drivers Online" value={`${visibleOnlineDrivers.length}/${visibleDrivers.length}`} />
          <StatCard icon={Box} label="Products" value={stats?.total_products || 0} />
          <StatCard icon={AlertTriangle} label="Low Stock" value={stats?.low_stock_products || 0} alert={stats?.low_stock_products > 0} />
          <StatCard icon={DollarSign} label="Revenue" value={`$${(stats?.total_revenue || 0).toFixed(0)}`} />
        </div>

        {/* Alerts */}
        {(pendingOrders.length > 0 || lowStockProducts.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {pendingOrders.length > 0 && (
              <div
                className="p-4 border-2"
                style={{
                  background: `${urgencyColor}1A`,
                  borderColor: urgencyColor,
                  // Subtle pulse animation when red
                  animation: oldestPendingAgeMin >= 7 ? 'pulse 1.5s ease-in-out infinite' : undefined,
                }}
                data-testid="pending-orders-alert"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5" style={{ color: urgencyColor }} />
                    <span className="font-mono" style={{ color: urgencyColor }}>
                      {pendingOrders.length} PENDING ORDER{pendingOrders.length > 1 ? 'S' : ''}
                      {oldestPendingAgeMin > 0 && (
                        <span className="ml-2 text-[#A3C7A8]">
                          · OLDEST {oldestPendingAgeMin}m
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Audio arm button — shows once until armed */}
                    {!audio.armed && (
                      <button
                        onClick={audio.arm}
                        className="font-mono text-[10px] uppercase px-2 py-1 border border-[#FF9F0A] text-[#FF9F0A] hover:bg-[#FF9F0A]/10 rounded-none"
                        data-testid="audio-arm-btn"
                      >
                        🔔 ENABLE AUDIO ALERTS
                      </button>
                    )}
                    {/* Audio toggle */}
                    {audio.armed && (
                      <button
                        onClick={toggleAudio}
                        className={`font-mono text-[10px] uppercase px-2 py-1 border rounded-none ${
                          audioEnabled
                            ? 'border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10'
                            : 'border-[#6B8E73] text-[#6B8E73] hover:bg-[#6B8E73]/10'
                        }`}
                        data-testid="audio-toggle-btn"
                      >
                        {audioEnabled ? '🔔 AUDIO: ON' : '🔕 AUDIO: OFF'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            {lowStockProducts.length > 0 && (
              <div className="bg-[rgba(255,59,48,0.1)] border border-[#FF3B30] p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
                  <span className="text-[#FF3B30] font-mono">
                    {lowStockProducts.length} PRODUCT{lowStockProducts.length > 1 ? 'S' : ''} LOW ON STOCK
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Feb 2026 (Slice 2 · step 4) — Recently Viewed Customers strip.
            Self-hides when empty. Tap a chip to re-open that customer's drawer. */}
        <div className="mb-4">
          <RecentlyViewedCustomersStrip from="operations-home" />
        </div>

        {/* Main Content */}
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-6">
          {/* Horizontally scrollable on mobile so it never overflows. Hide scrollbar visually. */}
          <div className="-mx-4 sm:mx-0 overflow-x-auto scrollbar-hide">
            <TabsList className="bg-[#0C0612] border border-[#2A0F3D] p-1 rounded-none inline-flex w-max sm:w-auto whitespace-nowrap mx-4 sm:mx-0">
              <TabsTrigger
                value="orders"
                className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-[#050003] text-[#A3C7A8] rounded-none font-mono"
                data-testid="tab-orders"
              >
                <Package className="w-4 h-4 mr-2" />
                <span>ORDERS</span>
              </TabsTrigger>
              <TabsTrigger
                value="livemap"
                className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-[#050003] text-[#A3C7A8] rounded-none font-mono"
                data-testid="tab-livemap"
              >
                <Map className="w-4 h-4 mr-2" />
                <span>LIVE MAP</span>
              </TabsTrigger>
              <TabsTrigger
                value="drivers"
                className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-[#050003] text-[#A3C7A8] rounded-none font-mono"
                data-testid="tab-drivers"
              >
                <Users className="w-4 h-4 mr-2" />
                <span>DRIVERS</span>
              </TabsTrigger>
              {kitsEnabled && (
                <TabsTrigger
                  value="kits"
                  className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-[#050003] text-[#A3C7A8] rounded-none font-mono"
                  data-testid="tab-kits"
                >
                  <Box className="w-4 h-4 mr-2" />
                  <span>FLEET KITS</span>
                </TabsTrigger>
              )}
              <TabsTrigger
                value="inventory"
                className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-[#050003] text-[#A3C7A8] rounded-none font-mono"
                data-testid="tab-inventory"
              >
                <Box className="w-4 h-4 mr-2" />
                <span>QUICK INVENTORY</span>
              </TabsTrigger>
              <TabsTrigger
                value="activity"
                className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-[#050003] text-[#A3C7A8] rounded-none font-mono"
                data-testid="tab-activity"
              >
                <Radio className="w-4 h-4 mr-2" />
                <span>ACTIVITY</span>
              </TabsTrigger>
              <TabsTrigger
                value="inbox"
                className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-[#050003] text-[#A3C7A8] rounded-none font-mono"
                data-testid="tab-inbox"
              >
                <Inbox className="w-4 h-4 mr-2" />
                <span>INBOX</span>
              </TabsTrigger>
              <TabsTrigger
                value="chat"
                className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-[#050003] text-[#A3C7A8] rounded-none font-mono"
                data-testid="tab-chat"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                <span>CHAT</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="orders" className="space-y-3">
            {/* Filter bar + bulk actions toolbar (Phase 5.9.10) */}
            {(() => {
              const cancelledCount = visibleOrders.filter(o => o.status === 'cancelled').length;
              const deliveredCount = visibleOrders.filter(o => o.status === 'delivered').length;
              return (
                <div className="flex items-stretch gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <OrderFiltersBar
                      filters={orderFilters}
                      onChange={setOrderFilters}
                      drivers={visibleDrivers}
                      resultCount={filteredOrders.length}
                      isSuperAdmin={isSuperAdmin}
                      selectedRegion={selectedRegion}
                      onRegionChange={setSelectedRegion}
                    />
                  </div>
                  <div className="flex items-start pt-2">
                    <OrderSortToggle value={orderSort} onChange={(v) => { setOrderSort(v); localStorage.setItem('dispatch_order_sort', v); }} />
                  </div>
                  <div className="flex items-start pt-2">
                    <BulkActionsMenu
                      enabled={deleteOrderEnabled}
                      cancelledCount={cancelledCount}
                      deliveredCount={deliveredCount}
                      onDeleteCancelled={() => handleBulkDelete('cancelled')}
                      onDeleteOldDelivered={() => handleBulkDelete('delivered', 7)}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Orders Table */}
            <div className="bg-[#0C0612] border border-[#2A0F3D]">
              {/* Column header — desktop only */}
              <div className="hidden md:grid grid-cols-12 gap-4 p-4 border-b border-[#2A0F3D] text-[#6B8E73] font-mono text-xs uppercase">
                <div className="col-span-2">Order ID</div>
                <div className="col-span-2">Customer</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Total</div>
                <div className="col-span-2">Driver</div>
                <div className="col-span-2">Actions</div>
              </div>

              {filteredOrders.length === 0 && countActiveFilters(orderFilters) > 0 && (
                <div className="p-8 text-center text-[#6B8E73] font-mono text-sm" data-testid="orders-empty-filtered">
                  // no orders match the current filter
                  <br />
                  <button
                    type="button"
                    onClick={() => setOrderFilters(EMPTY_FILTERS)}
                    className="mt-2 underline text-[#39FF14] hover:text-[#4AF626] text-xs"
                  >
                    clear filters
                  </button>
                </div>
              )}

              {sortedOrders.slice(0, 200).map(order => {
                const flash = flashingIds[order.id];
                const dueSoon = isDueSoon(order, nowTick);
                const flashTick = flashTicks[order.id] || 0;
                // Row-level classes: cyan/green background wash + border-l
                // (existing behavior) plus `relative` so the ring overlay
                // can absolute-position inside it.
                const flashClass = flash === 'new'
                  ? 'bg-[rgba(57,255,20,0.14)] border-l-4 border-l-[#39FF14] flash-new relative'
                  : flash === 'updated'
                    ? 'bg-[rgba(0,240,255,0.1)] border-l-4 border-l-[#00F0FF] flash-updated relative'
                    : dueSoon ? 'bg-[rgba(255,159,10,0.07)] border-l-4 border-l-[#FF9F0A]' : '';
                return (
                <div
                  key={order.id}
                  className={`flex flex-col gap-3 md:grid md:grid-cols-12 md:gap-4 md:items-center p-4 border-b border-[#2A0F3D] hover:bg-[#111A14] transition-colors ${flashClass}`}
                  data-testid={`dispatch-order-${order.id}`}
                  data-sse-live={flash ? '1' : undefined}
                >
                  {/* Feb 2026 — Fluidity LIVE ring overlay. Absolutely
                      positioned INSIDE the row; keyed by `flashTick` so a
                      new SSE event unmounts + remounts this thin div and
                      the CSS `flash-live` animation restarts from t=0.
                      Rendered only while `flash` is truthy so it doesn't
                      pollute the DOM tree when the row is idle. */}
                  {flash && (
                    <div
                      key={`live-${flashTick}`}
                      className="pointer-events-none absolute inset-0 flash-live"
                      aria-hidden="true"
                      data-testid={`dispatch-order-live-ring-${order.id}`}
                    />
                  )}
                  {/* Row 1 on mobile: order id + status badge (top right) */}
                  <div className="md:col-span-2 flex md:block items-center justify-between gap-2">
                    <div>
                      <button
                        type="button"
                        onClick={() => setEditingOrder(order)}
                        className="text-[#39FF14] font-mono text-sm hover:underline focus:outline-none focus:underline"
                        data-testid={`open-order-${order.id}`}
                        title={['pending', 'confirmed', 'preparing'].includes(order.status) ? 'View / edit order' : 'View order (read-only)'}
                      >
                        #{order.id.slice(0, 8).toUpperCase()}
                      </button>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <p className="text-[#4B6B52] font-mono text-xs">
                          {formatOrderTimestamp(order.created_at)}
                        </p>
                        {order.region && (
                          <Badge
                            variant="outline"
                            className="px-1 py-0 text-[9px] rounded-none font-mono uppercase border-[#A3C7A8] text-[#A3C7A8]"
                            data-testid={`order-region-${order.id}`}
                          >
                            {order.region}
                          </Badge>
                        )}
                        <DeliverySlotBadge order={order} testId={`order-slot-${order.id}`} />
                        {dueSoon && (
                          <span className="px-1 text-[9px] font-mono font-bold uppercase bg-[#FF9F0A] text-[#050003] whitespace-nowrap" data-testid={`order-due-soon-${order.id}`}>⚡ DUE SOON</span>
                        )}
                      </div>
                    </div>
                    {/* Status badge floats to right on mobile, lives in its own grid cell on desktop */}
                    <div className="md:hidden">
                      <Badge className={`${getStatusBadge(order.status)} border rounded-none font-mono text-xs uppercase`}>
                        {formatOrderStatus(order.status)}
                      </Badge>
                      {order.payment_method && order.payment_method !== 'cash' && (
                        <p className={`mt-1 font-mono text-[10px] uppercase text-right ${order.payment_status === 'paid' ? 'text-[#39FF14]' : 'text-[#FF9F0A]'}`}
                           data-testid={`payment-badge-${order.id}-m`}>
                          {order.payment_status === 'paid' ? '✓ PAID' : '⌛ ' + order.payment_method.toUpperCase()}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Customer + total on one row (mobile) */}
                  <div className="md:col-span-2 flex md:block items-center justify-between gap-2">
                    {order.customer_id ? (
                      <Link
                        to={`/dispatch/admin/users?openUser=${order.customer_id}&from=dispatch`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[#A3C7A8] hover:text-[#39FF14] font-mono text-sm truncate hover:underline"
                        data-testid={`dispatch-order-customer-link-${order.id}`}
                      >
                        {order.customer_name}
                      </Link>
                    ) : (
                      <p className="text-[#A3C7A8] font-mono text-sm truncate">{order.customer_name}</p>
                    )}
                    {/* On mobile, surface the total here so it sits with customer */}
                    <p className="md:hidden text-[#39FF14] font-mono font-bold whitespace-nowrap">${Number(order.total || 0).toFixed(2)}</p>
                  </div>

                  {/* Desktop status cell — hidden on mobile (already shown next to order id) */}
                  <div className="hidden md:block md:col-span-2">
                    <Badge className={`${getStatusBadge(order.status)} border rounded-none font-mono text-xs uppercase`}>
                      {formatOrderStatus(order.status)}
                    </Badge>
                    {order.payment_method && order.payment_method !== 'cash' && (
                      <p className={`mt-1 font-mono text-[10px] uppercase ${order.payment_status === 'paid' ? 'text-[#39FF14]' : 'text-[#FF9F0A]'}`}
                         data-testid={`payment-badge-${order.id}`}>
                        {order.payment_status === 'paid' ? '✓ PAID' : '⌛ ' + order.payment_method.toUpperCase()}
                      </p>
                    )}
                    {/* Feb 2026 — Live driver→customer distance + ETA.
                        Shown only while out_for_delivery AND we have both
                        endpoints on the order. Refreshes on each SSE
                        `order.location_updated` event (no API call). Source
                        indicator: green dot = server Mapbox ETA is fresh;
                        amber dot = client-side heuristic fallback. */}
                    {order.status === 'out_for_delivery' && (() => {
                      const eta = driverToCustomerEta(order);
                      if (eta.source === 'none') return null;
                      const dotClass = eta.source === 'server' ? 'bg-[#39FF14]' : 'bg-[#FF9F0A]';
                      return (
                        <p
                          className="mt-1 font-mono text-[10px] uppercase text-[#A3C7A8] flex items-center gap-1"
                          data-testid={`driver-eta-${order.id}`}
                          title={eta.source === 'server' ? 'Mapbox driving-traffic ETA' : 'Client-side heuristic ETA'}
                        >
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotClass}`} />
                          🚗 {Number(eta.miles ?? 0).toFixed(1)} MI · ~{eta.minutes} MIN
                        </p>
                      );
                    })()}
                  </div>

                  {/* Desktop total cell — hidden on mobile (shown alongside customer) */}
                  <div className="hidden md:block md:col-span-2">
                    <p className="text-[#39FF14] font-mono">${Number(order.total || 0).toFixed(2)}</p>
                  </div>

                  <div className="md:col-span-2 w-full">
                    {(() => {
                      const editable = ['pending', 'confirmed', 'preparing'].includes(order.status);
                      const showPicker = editable && (!order.driver_id || reassigning[order.id]);

                      if (!editable) {
                        return order.driver_id
                          ? <p className="text-[#A3C7A8] font-mono text-sm">{order.driver_name}</p>
                          : <span className="text-[#4B6B52] font-mono text-xs">N/A</span>;
                      }

                      if (!showPicker) {
                        // Assigned, editable — show name with CHANGE button
                        return (
                          <div className="flex items-center gap-2">
                            <p className="text-[#A3C7A8] font-mono text-sm truncate flex-1">{order.driver_name}</p>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setReassigning(prev => ({ ...prev, [order.id]: true }))}
                              className="h-7 px-2 border-[#2A0F3D] text-[#6B8E73] hover:border-[#FF9F0A] hover:text-[#FF9F0A] rounded-none text-xs font-mono"
                              data-testid={`reassign-driver-${order.id}`}
                              title="Reassign driver"
                            >
                              CHANGE
                            </Button>
                          </div>
                        );
                      }

                      // Picker (unassigned or dispatcher wants to change)
                      return (
                        <div className="flex flex-col gap-1">
                          {/* Currently-assigned banner (so dispatcher knows the order is NOT yet unassigned) */}
                          {order.driver_id && (
                            <div className="flex items-center justify-between gap-1 px-2 py-1 border border-[#FF9F0A] bg-[rgba(255,159,10,0.06)]">
                              <span className="font-mono text-[10px] text-[#FF9F0A] truncate">
                                CURRENT: <span className="text-[#A3C7A8]">{order.driver_name || '—'}</span>
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUnassignDriver(order.id, order.driver_name)}
                                className="h-6 px-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-[#050003] rounded-none text-[10px] font-mono"
                                data-testid={`unassign-driver-${order.id}`}
                                title="Remove driver from this order"
                              >
                                UNASSIGN
                              </Button>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                          <Select onValueChange={(driverId) => handleAssignDriver(order.id, driverId)}>
                            <SelectTrigger className="h-8 bg-[#050003] border-[#FF9F0A] text-[#FF9F0A] font-mono text-xs rounded-none flex-1" data-testid={`assign-driver-${order.id}`}>
                              <SelectValue placeholder={order.driver_id ? 'PICK NEW DRIVER' : 'ASSIGN'} />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
                              {rankedDriversForOrder(order.id).map(({ driver, miles, coverage }) => (
                                <SelectItem
                                  key={driver.id}
                                  value={driver.id}
                                  className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]"
                                >
                                  <span className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14]" />
                                    {driver.name}
                                    {driver.id === order.driver_id && (
                                      <span className="text-[#00F0FF] text-[10px]">· current</span>
                                    )}
                                    {miles != null && (
                                      <span className="text-[#6B8E73] text-xs">· {Number(miles ?? 0).toFixed(1)} mi</span>
                                    )}
                                    {coverage && (
                                      coverage.covers
                                        ? <span className="text-[#39FF14] text-[10px]">· kit ✓</span>
                                        : <span className="text-[#FF9F0A] text-[10px]" title={`Missing: ${coverage.missing.map(m => m.name || m.product_id).join(', ')}`}>· needs restock</span>
                                    )}
                                  </span>
                                </SelectItem>
                              ))}
                              {drivers.filter(d => !d.online).map(driver => (
                                <SelectItem
                                  key={driver.id}
                                  value={driver.id}
                                  className="text-[#6B8E73] font-mono focus:bg-[#2A0F3D] opacity-60"
                                >
                                  <span className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#4B6B52]" />
                                    {driver.name}
                                    {driver.id === order.driver_id && (
                                      <span className="text-[#00F0FF] text-[10px]">· current</span>
                                    )}
                                    <span className="text-[#4B6B52] text-xs">· offline</span>
                                  </span>
                                </SelectItem>
                              ))}
                              {visibleDrivers.length === 0 && (
                                <div className="px-2 py-1 text-[#4B6B52] font-mono text-xs">No drivers</div>
                              )}
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            onClick={() => handleAutoAssignClosest(order.id)}
                            disabled={visibleOnlineDrivers.length === 0 || !autoAssignEnabled}
                            title={!autoAssignEnabled ? 'Auto-assign disabled in Settings' : (visibleOnlineDrivers.length === 0 ? 'No online drivers' : 'Auto-assign closest online driver')}
                            className={`h-8 rounded-none text-xs px-2 disabled:opacity-40 ${autoAssignEnabled ? 'bg-[#39FF14] text-[#050003] hover:bg-[#4AF626]' : 'bg-[#2A0F3D] text-[#4B6B52]'}`}
                            data-testid={`auto-assign-${order.id}`}
                          >
                            <Zap className="w-3 h-3" />
                          </Button>
                          {order.driver_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setReassigning(prev => { const n = { ...prev }; delete n[order.id]; return n; })}
                              className="h-8 px-2 border-[#2A0F3D] text-[#6B8E73] hover:border-[#FF3B30] hover:text-[#FF3B30] rounded-none text-xs font-mono"
                              data-testid={`cancel-reassign-${order.id}`}
                              title="Cancel reassign"
                            >
                              <XCircle className="w-3 h-3" />
                            </Button>
                          )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="md:col-span-2 flex gap-2 flex-wrap">
                    {order.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusUpdate(order.id, 'confirmed')}
                        className="bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] rounded-none text-xs h-8"
                        data-testid={`confirm-order-${order.id}`}
                      >
                        CONFIRM
                      </Button>
                    )}
                    {order.status === 'confirmed' && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusUpdate(order.id, 'preparing')}
                        className="bg-[#00F0FF] text-[#050003] hover:bg-[#00F0FF]/80 rounded-none text-xs h-8"
                        data-testid={`prepare-order-${order.id}`}
                      >
                        PREPARING
                      </Button>
                    )}
                    {order.status === 'preparing' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStatusUpdate(order.id, 'confirmed')}
                        className="border-[#00F0FF] text-[#00F0FF] hover:bg-[rgba(0,240,255,0.1)] rounded-none text-xs h-8"
                        data-testid={`revert-confirmed-${order.id}`}
                        title="Move back to CONFIRMED"
                      >
                        ← CONFIRMED
                      </Button>
                    )}
                    {['pending', 'confirmed', 'preparing'].includes(order.status) && editOrderEnabled && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingOrder(order)}
                        className="border-[#FF9F0A] text-[#FF9F0A] hover:bg-[rgba(255,159,10,0.1)] rounded-none text-xs h-8"
                        data-testid={`edit-items-${order.id}`}
                        title="Edit order items"
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                    )}
                    {['pending', 'confirmed', 'preparing'].includes(order.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (window.confirm(`Cancel order #${order.id.slice(0, 8).toUpperCase()}? Stock will be restored.`)) {
                            handleStatusUpdate(order.id, 'cancelled');
                          }
                        }}
                        className="border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] rounded-none text-xs h-8"
                        data-testid={`cancel-order-${order.id}`}
                      >
                        <XCircle className="w-3 h-3" />
                      </Button>
                    )}
                    {/* Phase 5.5-Lite — MARK PAID / REFUNDED for cashapp orders */}
                    {order.payment_method && order.payment_method !== 'cash' && order.payment_status !== 'paid' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMarkPayment(order.id, 'paid')}
                        className="border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-[#050003] rounded-none text-xs h-8 px-2 font-mono"
                        data-testid={`mark-paid-${order.id}`}
                        title="Mark order as paid"
                      >
                        $ PAID
                      </Button>
                    )}
                    {order.payment_status === 'paid' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMarkPayment(order.id, 'refunded')}
                        className="border-[#FF9F0A] text-[#FF9F0A] hover:bg-[rgba(255,159,10,0.1)] rounded-none text-xs h-8 px-2 font-mono"
                        data-testid={`mark-refunded-${order.id}`}
                        title="Mark order as refunded"
                      >
                        ↩ REFUND
                      </Button>
                    )}
                    {deleteOrderEnabled && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteOrder(order.id)}
                        className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-[#050003] rounded-none text-xs h-8"
                        data-testid={`delete-order-${order.id}`}
                        title="Permanently delete this order (stock restored if pre-delivery)"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
                );
              })}

              {visibleOrders.length === 0 && (
                <div className="p-12 text-center">
                  <Package className="w-12 h-12 text-[#4B6B52] mx-auto mb-4" />
                  <p className="text-[#4B6B52] font-mono">// NO ORDERS</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="livemap" className="space-y-4">
            <div className="bg-[#0C0612] border border-[#2A0F3D] p-4">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <h2 className="text-[#39FF14] font-mono text-lg font-bold flex items-center gap-2">
                    <Map className="w-5 h-5" />
                    FLEET_TRACKING
                  </h2>
                  <p className="text-[#4B6B52] font-mono text-xs mt-1">
                    // All drivers online right now — location refreshes every 20s
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-[#A3C7A8] font-mono text-sm flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#39FF14] rounded-full status-pulse" />
                    {visibleOrders.filter(o => o.status === 'out_for_delivery').length} On Route
                  </span>
                  <span className="text-[#A3C7A8] font-mono text-sm flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#00F0FF] rounded-full" />
                    {visibleOnlineDrivers.filter(d => !visibleOrders.some(o => o.driver_id === d.id && o.status === 'out_for_delivery')).length} Idle
                  </span>
                </div>
              </div>
              <LiveDriverMap
                drivers={visibleDrivers}
                orders={visibleOrders}
                region={selectedRegion !== 'all' ? selectedRegion : user?.region}
              />
            </div>
          </TabsContent>

          <TabsContent value="drivers" className="space-y-4">
            <div className="flex items-center gap-4 mb-2">
              <span className="text-[#A3C7A8] font-mono text-sm flex items-center gap-2">
                <span className="w-2 h-2 bg-[#39FF14] rounded-full status-pulse" />
                {visibleOnlineDrivers.length} ONLINE
              </span>
              <span className="text-[#6B8E73] font-mono text-sm flex items-center gap-2">
                <span className="w-2 h-2 bg-[#4B6B52] rounded-full" />
                {visibleDrivers.length - visibleOnlineDrivers.length} OFFLINE
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibleDrivers.map(driver => {
                const activeCount = visibleOrders.filter(o => o.driver_id === driver.id && ['confirmed', 'preparing', 'out_for_delivery'].includes(o.status)).length;
                const lastSeenTxt = formatLastSeen(driver.last_seen);
                return (
                  <div key={driver.id} className={`bg-[#0C0612] border p-4 ${driver.online ? 'border-[#39FF14]' : 'border-[#2A0F3D]'}`} data-testid={`driver-card-${driver.id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#2A0F3D] flex items-center justify-center">
                          <Truck className={`w-5 h-5 ${driver.online ? 'text-[#39FF14]' : 'text-[#4B6B52]'}`} />
                        </div>
                        <div>
                          <p className={`font-mono font-bold ${driver.online ? 'text-[#39FF14]' : 'text-[#A3C7A8]'}`}>{driver.name}</p>
                          <p className="text-[#4B6B52] font-mono text-xs">{driver.email}</p>
                        </div>
                      </div>
                      <span className={`flex items-center gap-1 font-mono text-[10px] uppercase px-2 py-1 ${driver.online ? 'text-[#39FF14] bg-[rgba(57,255,20,0.08)] border border-[#39FF14]' : 'text-[#6B8E73] bg-[#050003] border border-[#2A0F3D]'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${driver.online ? 'bg-[#39FF14] status-pulse' : 'bg-[#4B6B52]'}`} />
                        {driver.online ? 'ONLINE' : 'OFFLINE'}
                      </span>
                    </div>
                    {driver.phone && (
                      <p className="text-[#A3C7A8] font-mono text-sm">Phone: {driver.phone}</p>
                    )}
                    <div className="mt-3 pt-3 border-t border-[#2A0F3D] flex items-center justify-between">
                      <p className="text-[#6B8E73] font-mono text-xs">
                        Active Orders: <span className="text-[#39FF14]">{activeCount}</span>
                      </p>
                      <p className="text-[#4B6B52] font-mono text-[10px]">
                        {driver.online ? 'Live now' : lastSeenTxt}
                      </p>
                    </div>
                    {driver.online && driver.current_location && (
                      <p className="text-[#4B6B52] font-mono text-[10px] mt-1">
                        <MapPin className="w-3 h-3 inline mr-1" />
                        {Number(driver.current_location.lat ?? 0).toFixed(4)}, {Number(driver.current_location.lng ?? 0).toFixed(4)}
                      </p>
                    )}
                  </div>
                );
              })}

              {visibleDrivers.length === 0 && (
                <div className="col-span-full bg-[#0C0612] border border-[#2A0F3D] p-12 text-center">
                  <Users className="w-12 h-12 text-[#4B6B52] mx-auto mb-4" />
                  <p className="text-[#4B6B52] font-mono">// NO DRIVERS REGISTERED</p>
                </div>
              )}
            </div>
          </TabsContent>

          {kitsEnabled && (
            <TabsContent value="kits" className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                <div>
                  <h2 className="text-[#39FF14] font-mono text-lg font-bold flex items-center gap-2">
                    <Box className="w-5 h-5" />
                    FLEET_KITS
                  </h2>
                  <p className="text-[#4B6B52] font-mono text-xs mt-1">
                    // Live driver kit inventory · {visibleKits.length} active · ${visibleKits.reduce((s, k) => s + kitTotalValue(k.items), 0).toFixed(2)} in motion
                  </p>
                </div>
                <Button
                  onClick={() => setOpenKitOpen(true)}
                  className="bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] rounded-none font-mono font-bold"
                  data-testid="open-kit-btn"
                >
                  <Box className="w-4 h-4 mr-2" />
                  OPEN NEW KIT
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {visibleKits.map(kit => {
                  const driverDoc = drivers.find(d => d.id === kit.driver_id);
                  const isOnline = driverDoc?.online;
                  const lowItems = kit.items.filter(i => i.qty_current > 0 && i.qty_current <= 2).length;
                  const emptyItems = kit.items.filter(i => i.qty_current === 0).length;
                  const lastSeen = kit.updated_at ? new Date(kit.updated_at) : null;
                  const minsAgo = lastSeen ? Math.floor((Date.now() - lastSeen.getTime()) / 60000) : null;
                  const isRestock = kit.mode === 'restock';
                  return (
                    <div
                      key={kit.id}
                      className={`relative overflow-hidden p-4 transition-shadow ${
                        isRestock
                          ? 'bg-gradient-to-br from-[#001A1F] to-[#0C0612] border-2 border-[#00F0FF] shadow-[0_0_16px_rgba(0,240,255,0.18)]'
                          : 'bg-[#0C0612] border-2 border-[#39FF14] shadow-[0_0_12px_rgba(57,255,20,0.10)]'
                      }`}
                      data-testid={`kit-card-${kit.driver_id}`}
                    >
                      {/* Mode banner — top strip */}
                      <div
                        className={`-mx-4 -mt-4 mb-3 px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] flex items-center justify-between ${
                          isRestock
                            ? 'bg-[#00F0FF] text-[#001A1F]'
                            : 'bg-[#39FF14] text-[#050003]'
                        }`}
                      >
                        <span>{isRestock ? '📦 RESTOCK MODE' : '🚚 DELIVERY MODE'}</span>
                        <span>{isRestock ? '· IN TRANSIT' : '· CUSTOMER-READY'}</span>
                      </div>

                      <div className="flex items-center justify-between mb-3">
                        <div className="min-w-0">
                          <p className="text-[#39FF14] font-mono font-bold truncate flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-[#39FF14] status-pulse' : 'bg-[#4B6B52]'}`} />
                            {kit.driver_name}
                          </p>
                          <p className="text-[#4B6B52] font-mono text-[10px] uppercase">
                            ${kitTotalValue(kit.items).toFixed(2)}
                            {minsAgo != null && ` · updated ${minsAgo}m ago`}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          {emptyItems > 0 && (
                            <span className="px-1.5 py-0.5 border border-[#FF3B30] text-[#FF3B30] font-mono text-[10px]" title="Items at zero">
                              {emptyItems} EMPTY
                            </span>
                          )}
                          {lowItems > 0 && (
                            <span className="px-1.5 py-0.5 border border-[#FF9F0A] text-[#FF9F0A] font-mono text-[10px]" title="Items low (≤2)">
                              {lowItems} LOW
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                        {kit.items.map(item => (
                          <KitItemBar key={item.product_id} item={item} />
                        ))}
                      </div>
                      {/* Action row */}
                      <div className="mt-3 pt-3 border-t border-[#2A0F3D] grid grid-cols-2 gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            const next = isRestock ? 'delivery' : 'restock';
                            try {
                              await axios.post(
                                `${API_URL}/api/drivers/${kit.driver_id}/mode`,
                                { mode: next },
                                { withCredentials: true }
                              );
                              toast.success(`${kit.driver_name}: ${next.toUpperCase()} mode`);
                              fetchData();
                            } catch {
                              toast.error('Failed to switch mode');
                            }
                          }}
                          className={`rounded-none font-mono text-xs ${
                            isRestock
                              ? 'border-[#39FF14] text-[#39FF14] hover:bg-[rgba(57,255,20,0.08)]'
                              : 'border-[#00F0FF] text-[#00F0FF] hover:bg-[rgba(0,240,255,0.08)]'
                          }`}
                          data-testid={`mode-toggle-${kit.driver_id}`}
                        >
                          → {isRestock ? 'DELIVERY' : 'RESTOCK'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setReconcileKit(kit)}
                          className="rounded-none font-mono text-xs border-[#FF9F0A] text-[#FF9F0A] hover:bg-[rgba(255,159,10,0.08)]"
                          data-testid={`reconcile-${kit.driver_id}`}
                        >
                          📋 RECONCILE
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {visibleKits.length === 0 && (
                  <div className="col-span-full bg-[#0C0612] border border-[#2A0F3D] p-12 text-center">
                    <Box className="w-12 h-12 text-[#4B6B52] mx-auto mb-4" />
                    <p className="text-[#4B6B52] font-mono">// NO ACTIVE KITS</p>
                    <p className="text-[#4B6B52] font-mono text-xs mt-2">Click "OPEN NEW KIT" to assign inventory to a driver</p>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          <TabsContent value="inventory" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {products.map(product => {
                // Phase 6 Tier 1 (Feb 2026) — region-scoped stock breakdown.
                // Primary number = region_stock[caller_region] = sellable count
                // for the regional dispatcher. SA / cross-region sees the
                // legacy global `product.stock` aggregate (sum across regions).
                const scopeRegion = stats?.region_scope || null;
                const sellable = scopeRegion
                  ? Number((product.region_stock || {})[scopeRegion] || 0)
                  : Number(product.stock || 0);
                const inKits = kitsEnabled ? visibleKits.reduce(
                  (s, k) => s + (k.items.find(i => i.product_id === product.id)?.qty_current || 0),
                  0,
                ) : 0;
                const inDepot = Math.max(0, sellable - inKits);
                const isLow = sellable < 10;
                return (
                  <div
                    key={product.id}
                    className={`bg-[#0C0612] border p-4 ${isLow ? 'border-[#FF3B30]' : 'border-[#2A0F3D]'}`}
                    data-testid={`inventory-product-${product.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[#39FF14] font-mono text-sm font-bold truncate">{product.name}</p>
                      {isLow && (
                        <AlertTriangle className="w-4 h-4 text-[#FF3B30]" />
                      )}
                    </div>
                    <p className="text-[#4B6B52] font-mono text-xs uppercase">{product.category}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-[#A3C7A8] font-mono text-sm">${Number(product.price ?? 0).toFixed(2)}</span>
                      <span className={`font-mono font-bold ${isLow ? 'text-[#FF3B30]' : 'text-[#39FF14]'}`}>
                        {sellable} SELLABLE
                      </span>
                    </div>
                    {(inKits > 0 || sellable > 0) && (
                      <p className="text-[#6B8E73] font-mono text-[10px] mt-1">
                        // {inDepot} in depot
                        {inKits > 0 && <span className="text-[#FF9F0A]"> · {inKits} in kits</span>}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="text-center pt-4">
              <Link to="/dispatch/inventory">
                <Button className="bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] rounded-none">
                  <Box className="w-4 h-4 mr-2" />
                  FULL INVENTORY MANAGEMENT
                </Button>
              </Link>
            </div>
          </TabsContent>

          <TabsContent value="activity" className="space-y-4">
            <ActivityFeedPanel
              isSuperAdmin={isSuperAdmin}
              selectedRegion={selectedRegion}
            />
          </TabsContent>

          <TabsContent value="inbox" className="space-y-4">
            <InboxPanel
              isSuperAdmin={isSuperAdmin}
              selectedRegion={selectedRegion}
            />
          </TabsContent>

          <TabsContent value="chat" className="space-y-4 pb-24 md:pb-0">
            <DispatcherChatPanel
              initialCustomerId={(() => {
                try { return new URLSearchParams(window.location.search).get('chat'); }
                catch { return null; }
              })()}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit/View order dialog. readOnly is enforced when status isn't in
          the pending/confirmed/preparing window, so dispatchers can still
          inspect post-fulfillment orders (delivered / cancelled) but can't
          mutate them. */}
      <EditOrderItemsDialog
        open={!!editingOrder}
        onOpenChange={(v) => { if (!v) setEditingOrder(null); }}
        order={editingOrder}
        products={products}
        onUpdated={fetchData}
        readOnly={!editingOrder || !['pending', 'confirmed', 'preparing'].includes(editingOrder.status) || !editOrderEnabled}
      />

      {/* Phase 5.8 — Kit reconciliation dialog */}
      <KitReconcileDialog
        kit={reconcileKit}
        onClose={() => setReconcileKit(null)}
        onApplied={fetchData}
      />

      {/* Open-kit dialog */}
      <OpenKitDialog
        open={openKitOpen}
        onOpenChange={setOpenKitOpen}
        drivers={drivers}
        products={products}
        onOpened={fetchData}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, alert }) {
  return (
    <div className={`bg-[#0C0612] p-4 ${alert ? 'border-l-2 border-[#FF3B30]' : ''}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${alert ? 'text-[#FF3B30]' : 'text-[#6B8E73]'}`} />
        <span className="text-[#6B8E73] font-mono text-xs uppercase">{label}</span>
      </div>
      <p className={`font-mono text-xl font-bold ${alert ? 'text-[#FF3B30]' : 'text-[#39FF14]'}`}>
        {value}
      </p>
    </div>
  );
}

function formatLastSeen(iso) {
  if (!iso) return 'Never seen';
  const then = new Date(iso);
  if (isNaN(then.getTime())) return 'Never seen';
  const diffMs = Date.now() - then.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Seen <1m ago';
  if (mins < 60) return `Seen ${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Seen ${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `Seen ${days}d ago`;
}
